// Parts shared by the tonal call review (CallReview) and the pulsed review (PulsedReview): the Call type card
// (species, broad class, code with its meaning, WC cheat sheet) and the decision bar (Reset, Dismiss with a reason,
// Confirm). 7 Oct 2026.
import React, { useState } from 'react';
import WcCodeFinder from '../WcCodeFinder';
import BaleenCodeFinder from './BaleenCodeFinder';
import { LibraryExamples, useSectionLibrary } from './Library';
import WC from '../wcCodes.json';
import CODES from './codes.json';
import { SECTION, SPECIES_LIST, speciesName } from './sections';
import { DISMISS_REASONS } from './reviewModel';
import { Info } from './info';

export function codeMeaning(system, code) {
  if (!code) return null;
  if (system === 'WC') return WC[code] || null;
  return (CODES[system] || {})[code] || null;
}
export function codeList(section) {
  return Object.keys(section.codeSystem === 'WC' ? WC : (CODES[section.codeSystem] || {}))
    .filter((k) => (!section.codePrefix || k.startsWith(section.codePrefix)) && (!section.codeFilter || section.codeFilter(k)));
}

export function statusChip(s) {
  if (!s) return ['To review', ''];
  if (s === 'saving') return ['Saving…', ''];
  if (s === 'confirmed') return ['Final ✓', 'ok'];
  if (s === 'to_approve') return ['Sent for approval', 'ok'];
  if (s === 'dismissed') return ['Dismissed', 'off'];
  if (s === 'error') return ['Not saved', 'bad'];
  return [`Moved to ${SECTION[s.split(':')[1]]?.label || 'another section'}`, 'ok'];
}

function WcFinderWithLibrary({ library, code, onUse }) {
  const items = useSectionLibrary(library, 'humpback');
  return <WcCodeFinder code={code} onUse={onUse} extra={library ? (c) => <LibraryExamples library={library} items={items} code={c} /> : null} />;
}

/** label = {speciesId, broad, code}; setLabel(fn) changes it. */
export function TypeCard({ section, entry, label, setLabel, moveTo, call, library = null, title = 'Call type', children }) {
  const [finderOpen, setFinderOpen] = useState(false);
  const moving = moveTo && moveTo !== section.key;
  const list = codeList(section);
  return (
    <div className="ct-card">
      <h3>{title} <Info id="calltype" /></h3>
      <div className="hcr-form">
        <label htmlFor="cr-species" className="ct-muted">Species</label>
        <select id="cr-species" className={`hcr-input ${moving ? 'changed' : ''}`} value={label.speciesId || ''}
                onChange={(e) => setLabel((s) => { s.label.speciesId = e.target.value ? Number(e.target.value) : null; s.label.speciesLabel = speciesName(e.target.value); })}>
          <option value="">{entry.species ? `${entry.species} (engine)` : 'Not set'}</option>
          {SPECIES_LIST.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
        </select>
        {moving && <><span /><span className="cr-move">Saving moves this to <strong>{SECTION[moveTo].label}</strong>.</span></>}
        {section.broad && <>
          <label htmlFor="cr-broad" className="ct-muted">Broad class</label>
          <select id="cr-broad" className="hcr-input" value={label.broad} onChange={(e) => setLabel((s) => { s.label.broad = e.target.value; })}>
            {section.broad.map((b) => <option key={b} value={b}>{b}</option>)}</select></>}
        {section.codeSystem && <>
          <label htmlFor="cr-code" className="ct-muted">Code ({section.codeSystem === 'PAM' ? 'Code document' : section.codeSystem})</label>
          <input id="cr-code" list="cr-codes" className={`hcr-input hcr-mono ${label.code !== (entry.code || '') ? 'changed' : ''}`} value={label.code}
                 onChange={(e) => setLabel((s) => { s.label.code = e.target.value.trim(); })} />
          <datalist id="cr-codes">{list.map((k) => <option key={k} value={k}>{codeMeaning(section.codeSystem, k)}</option>)}</datalist>
          <span className="ct-muted">Means</span>
          <span className="hcr-small">{codeMeaning(section.codeSystem, label.code) || 'not a code in the list'}</span>
          <span className="ct-muted">Engine said</span><span className="hcr-mono hcr-small">{entry.code || '—'}</span></>}
      </div>
      {section.finder === 'WC' && (
        <details className="hcr-finder" open={finderOpen} onToggle={(e) => setFinderOpen(e.currentTarget.open)}>
          <summary>Find the code yourself (WC cheat sheet)</summary>
          {finderOpen && <WcFinderWithLibrary library={library} code={label.code} onUse={(code) => setLabel((s) => { s.label.code = code; })} />}
        </details>)}
      {section.finder === 'PAM' && (
        <details className="hcr-finder" open={finderOpen} onToggle={(e) => setFinderOpen(e.currentTarget.open)}>
          <summary>Find the code yourself (baleen cheat sheet)</summary>
          {finderOpen && <BaleenCodeFinder code={label.code} species={label.speciesLabel || entry.species} call={call}
            library={library} onUse={(code) => setLabel((s) => { s.label.code = code; })} />}
        </details>)}
      {children}
    </div>
  );
}

/** Reset · Dismiss… (reason) · Confirm. confirmText comes from the reviewer's role (approval step). */
export function DecisionBar({ onReset, onSubmit, saving, saved, confirmText, what = 'call', note, error }) {
  const [open, setOpen] = useState(false);
  const [dismiss, setDismiss] = useState({ reason: 'faint', note: '' });
  return (
    <>
      {open ? (
        <fieldset className="cr-dismiss">
          <legend>Why dismiss this {what}?</legend>
          {DISMISS_REASONS.map(([k, label]) => (
            <label key={k} className="ct-check"><input type="radio" name="cr-dismiss" checked={dismiss.reason === k}
              onChange={() => setDismiss({ ...dismiss, reason: k })} /> {label}</label>))}
          <textarea className="hcr-input" rows={2} placeholder="Note (optional)" value={dismiss.note}
                    onChange={(e) => setDismiss({ ...dismiss, note: e.target.value })} />
          <p className="ct-muted hcr-small">Dismissed items are saved with the reason (useful to teach the engine what to skip) and are not shown again.</p>
          <div className="ct-row">
            <button type="button" className="ct-btn" onClick={() => setOpen(false)}>Cancel</button>
            <button type="button" className="ct-btn cr-danger" onClick={async () => { await onSubmit({ status: 'dismissed', ...dismiss }); setOpen(false); }}>Dismiss {what}</button>
          </div>
        </fieldset>
      ) : (
        <div className="ct-row hcr-actions">
          <button type="button" className="ct-btn" onClick={onReset}>Reset to engine</button>
          <button type="button" className="ct-btn cr-danger" onClick={() => setOpen(true)}>Dismiss…</button>
          <button type="button" className="ct-btn green" disabled={saving} onClick={() => onSubmit({ status: 'confirmed' })}>
            {saved ? 'Saved ✓' : confirmText}</button>
        </div>)}
      {note && <p className="ct-muted hcr-small cr-note">{note}</p>}
      {error && <p className="cr-error" role="alert">{error}</p>}
    </>
  );
}


/** Dismiss several items at once (10 Oct 2026): tick them in the list, pick one reason, Dismiss. Each is saved like
 * a single dismiss (reason kept for the engine's tuning). makeDoc(item, decision) builds the item's review doc. */
export function BulkDismiss({ calls, ticked, setTicked, status, setStatus, makeDoc, onSubmit }) {
  const [reason, setReason] = useState('faint');
  const [busy, setBusy] = useState('');
  const items = calls.filter((x) => ticked.has(x.id) && !status[x.id]);
  if (!ticked.size) return null;
  const run = async () => {
    let n = 0;
    for (const x of items) {
      setBusy(`Dismissing ${n + 1} of ${items.length}…`);
      setStatus((s) => ({ ...s, [x.id]: 'saving' }));
      try { if (onSubmit) await onSubmit(x, makeDoc(x, { status: 'dismissed', reason, note: 'dismissed with others' })); setStatus((s) => ({ ...s, [x.id]: 'dismissed' })); n += 1; }
      catch (e) { setStatus((s) => ({ ...s, [x.id]: 'error' })); }
    }
    setBusy(`${n} dismissed.`); setTicked(new Set());
  };
  return (
    <div className="hcr-bulk">
      <strong>{items.length} ticked</strong>
      <label className="hcr-small">Reason<select className="hcr-input" value={reason} onChange={(e) => setReason(e.target.value)}>
        {DISMISS_REASONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
      <button type="button" className="ct-btn cr-danger" disabled={!items.length || busy.startsWith('Dismissing')} onClick={run}>Dismiss {items.length}</button>
      <button type="button" className="ct-btn" onClick={() => setTicked(new Set())}>Clear</button>
      {busy && <span className="ct-muted hcr-small">{busy}</span>}
    </div>);
}

/** the tick box in front of a list item */
export function Tick({ id, ticked, setTicked, disabled }) {
  return <input type="checkbox" className="hcr-tick" aria-label="Tick to dismiss with others" disabled={disabled} checked={ticked.has(id)}
                onChange={() => setTicked((t) => { const n = new Set(t); if (n.has(id)) n.delete(id); else n.add(id); return n; })} />;
}
